var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useEffect, useReducer } from 'react';
import { MaskedTextField } from '../Components/MaskedTextField';
import { useDebounce } from '../Hooks/useDebounce';
import { ApplicationStorageKeys, loadFromLocalStorage, saveToLocalStorage } from '../Shared/LocalStorage';
import { divider } from '../Styles/Colours';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import produce from 'immer';
import { cloneDeep } from 'lodash';
import { Button, ButtonVariant, ButtonMode, ButtonSize } from '../Components/Button';
import { ButtonGroup } from '../Components/ButtonGroup';
import { MedicinalProductSearchBy } from '../Contracts/MedicationModels';
import Radio from '@material-ui/core/Radio/Radio';
import styles from './MedicationsBrowserFilter.module.css';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
var clearFilterDefaults = {
    productName: '',
    medicinalProductSearchBy: MedicinalProductSearchBy.All,
    limit: 250,
    showNonTga: true,
    showOnlySubsidisedMedications: false
};
var filterReducer = produce(function (state, action) {
    switch (action.type) {
        case 'UpdateField':
            state[action.field] = action.value;
            break;
        case 'UpdateMedicinalProductSearchBy':
            state.medicinalProductSearchBy = action.value;
            break;
        case 'UpdateLimit':
            state.limit = action.value;
            break;
        case 'UpdateShowNonTga':
            state.showNonTga = action.value;
            break;
        case 'UpdateShowOnlySubsidisedMedications':
            state.showOnlySubsidisedMedications = action.value;
            break;
        case 'Clear':
            return cloneDeep(clearFilterDefaults);
    }
});
export function useMedicationsBrowserFilter(filterUpdatedCallback, defaultFilter) {
    var _a = __read(useReducer(filterReducer, __assign(__assign({}, loadFromLocalStorage(ApplicationStorageKeys.MedicationsBrowserFilters, cloneDeep(clearFilterDefaults))), (defaultFilter !== null && defaultFilter !== void 0 ? defaultFilter : {}))), 2), state = _a[0], dispatch = _a[1];
    useEffect(function () {
        saveToLocalStorage(ApplicationStorageKeys.MedicationsBrowserFilters, __assign(__assign({}, state), { productName: '' }));
    }, [state]);
    var debouncedState = useDebounce(state);
    useEffect(function () {
        filterUpdatedCallback(debouncedState);
    }, [debouncedState]);
    var updateField = function (field) { return function (value) {
        dispatch({ type: 'UpdateField', field: field, value: value });
    }; };
    var clearField = function (field, clearValue) { return function () {
        dispatch({ type: 'UpdateField', field: field, value: clearValue });
    }; };
    var updateMedicinalProductSearchBy = function (value) { return function () {
        dispatch({ type: 'UpdateMedicinalProductSearchBy', value: value });
    }; };
    var updateLimit = function (value) { return function () {
        dispatch({ type: 'UpdateLimit', value: value });
    }; };
    var updateShowNonTga = function (value) { return function () {
        dispatch({ type: 'UpdateShowNonTga', value: value });
    }; };
    var updateShowOnlySubsidisedMedications = function (value) { return function () {
        dispatch({ type: 'UpdateShowOnlySubsidisedMedications', value: value });
    }; };
    var clearFilter = function () {
        dispatch({ type: 'Clear' });
    };
    return {
        state: state,
        updateField: updateField,
        clearField: clearField,
        updateMedicinalProductSearchBy: updateMedicinalProductSearchBy,
        updateLimit: updateLimit,
        updateShowNonTga: updateShowNonTga,
        updateShowOnlySubsidisedMedications: updateShowOnlySubsidisedMedications,
        clearFilter: clearFilter
    };
}
export var MedicationsBrowserFilter = function (props) {
    return (React.createElement("div", { className: styles.containFilters },
        React.createElement("div", { className: styles.controls },
            React.createElement("div", { className: styles.filterTitle }, "Filter"),
            React.createElement("div", { className: styles.filterRow },
                React.createElement(MaskedTextField, { name: 'produceName', label: 'Product name', testId: 'input-product-name', autoFocus: true, helperText: 'Search by generic or brand name', value: props.state.productName, onChange: function (name) {
                        // Limited to 254 characters since that's the max length we store in the product short name.
                        // Any more character could not be found in the shortname and would return no results.
                        props.updateField('productName')(name.substr(0, 254));
                    }, onClear: props.clearField('productName', '') })),
            React.createElement("div", { className: styles.filterRow, "data-test-id": 'radio-group-search-by' },
                React.createElement("div", { className: styles.searchBy }, "Search by"),
                React.createElement(FormControlLabel, { className: styles.drugRadio, label: 'Generics only', labelPlacement: 'end', control: React.createElement(Radio, { checked: props.state.medicinalProductSearchBy ===
                            MedicinalProductSearchBy.MedicinalProductPackOnly, onChange: props.updateMedicinalProductSearchBy(MedicinalProductSearchBy.MedicinalProductPackOnly) }) }),
                React.createElement(FormControlLabel, { className: styles.drugRadio, label: 'Brands only', labelPlacement: 'end', control: React.createElement(Radio, { checked: props.state.medicinalProductSearchBy ===
                            MedicinalProductSearchBy.TradeProductPackOnly, onChange: props.updateMedicinalProductSearchBy(MedicinalProductSearchBy.TradeProductPackOnly) }) }),
                React.createElement(FormControlLabel, { className: styles.drugRadio, label: 'All', labelPlacement: 'end', control: React.createElement(Radio, { checked: props.state.medicinalProductSearchBy === MedicinalProductSearchBy.All, onChange: props.updateMedicinalProductSearchBy(MedicinalProductSearchBy.All) }) })),
            React.createElement("div", { className: styles.filterRow, "data-test-id": 'checkbox-group-filter-tga-payment-scheme' },
                React.createElement(FormControlLabel, { className: styles.drugCheckbox, label: 'Show PBS/RPBS only', labelPlacement: 'end', control: React.createElement(Checkbox, { checked: props.state.showOnlySubsidisedMedications, onChange: props.updateShowOnlySubsidisedMedications(!props.state.showOnlySubsidisedMedications) }) })),
            React.createElement("div", { className: styles.showResultTitle }, "Show max results"),
            React.createElement("div", { className: styles.metricPeriodPicker },
                React.createElement(ButtonGroup, { borderColour: divider },
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 25 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 25 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(25), testId: 'filter-25' }, "25"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 50 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 50 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(50), testId: 'filter-50' }, "50"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 100 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 100 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(100), testId: 'filter-100' }, "100"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 250 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 250 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(250), testId: 'filter-250' }, "250")))),
        React.createElement("div", { className: styles.clearButtonContainer },
            React.createElement(Button, { onClick: props.clearFilter, size: ButtonSize.medium, testId: 'clear-filter', color: 'primary', variant: ButtonVariant.light, width: '100%' }, "Clear all filters"))));
};
